import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { textFormatter } from "@/shared/utils/textFormatter";
import { useEffect, useRef, useState } from "react";
import { UserAccountMenuProps } from "../types/profile";
import { AvatarButton } from "./AvatarButton";
import Card from "./Card";

export function UserAccountMenu({ fullName, email, role, actions }: UserAccountMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = () => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(
    () => () => {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    },
    []
  );
  const menuClasses = getThemeComponentGroup("menu");
  const name = textFormatter(fullName) || "My account";
  const parts = fullName?.trim().split(/\s+/).filter(Boolean) || [];
  const initials = parts.length
    ? `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase()
    : "U";

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="relative inline-block"
      onPointerEnter={(event) => {
        if (event.pointerType !== "mouse") return;
        cancelClose();
        setOpen(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== "mouse") return;
        cancelClose();
        closeTimer.current = setTimeout(() => {
          closeTimer.current = null;
          if (document.hasFocus() && !rootRef.current?.contains(document.activeElement)) {
            setOpen(false);
          }
        }, 250);
      }}
      onBlur={(event) => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.stopPropagation();
          setOpen(false);
          rootRef.current?.querySelector("button")?.focus();
        }
      }}
    >
      <AvatarButton
        initials={initials}
        fullName={name}
        role={role}
        menuOpen={open}
        onClick={() => setOpen((value) => !value)}
      />
      {open && (
        <Card className="absolute right-0 z-50 mt-3 w-50 max-w-sm overflow-hidden rounded-2xl! shadow-xl">
          <div className="space-y-0.5 p-2">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                disabled={action.disabled}
                onClick={() => {
                  setOpen(false);
                  action.onClick();
                }}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-60 ${action.danger ? "text-theme-danger hover:bg-theme-danger-bright-soft" : `${getThemeClass(menuClasses.itemTextColor, "text-theme-text-muted")} ${getThemeClass(menuClasses.itemHoverBgColor, "hover:bg-theme-surface-muted")}`} ${action.className ?? ""}`}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
